import getDeviceInfo from '../helpers/getDeviceInfo';
import {
  OwnershipTypes,
  proprietorTypes,
  SubscriptionProductTypes,
  SubscriptionStatusTypes,
  tenancyAgreementStatusTypes,
  tenancyDepositStatusTypes,
  TenancyStatusTypes,
  verificationStatusTypes,
} from './types';

export const profileDefault = () => ({
  emailAddress: '',
  password: '',
  firstName: '',
  lastName: '',
  fullName: '',
  hasDeleted: false,
  imageUrl: '',
  mobileNumber: '',
  profileType: null,
  profileUid: null,
  title: 'Ms',
});

export const publicAccountDefault = () => ({
  profileUid: null,
  address: {
    country: '',
    district: '',
    lat: '',
    line1: '',
    line2: '',
    long: '',
    name: '',
    postcode: '',
    street: '',
    town: '',
  },
  appInfo: getDeviceInfo(),
  banking: {
    accountMask: '',
    accountName: '',
    dateUpdated: null, // firestore.Timestamp
    plaidRecipientId: '',
  },
  dateCreated: null,
  dateUpdated: null,
  landlordPropertyUids: [],
  noticePreferences: {
    banking: true,
    messaging: true,
    payments: true,
    people: true,
    search: true,
    tenancy: true,
  },
  profileType: '',
  proprietor: {
    companyNumber: '',
    companyVATNumber: '',
    //  Should be collected at there landlord verification stage
    isDirector: true,
    director: {
      emailAddress: '',
      firstName: '',
      fullName: '',
      lastName: '',
      mobileNumber: '',
      title: '',
    },
    name: '',
    type: proprietorTypes.soleTrader,
  },
  savedPropertyUids: [],
  tenantTenancyUids: [],
  // stripe: {
  //   customer: '',
  //   dateCreated: null,
  // },
  verificationStatus: verificationStatusTypes.NOT_COMPLETED,
});

export const propertyDefault = () => ({
  bedroomCount: 1,
  bathroomCount: 1,
  bio: '',
  clauses: [],
  dateCreated: null,
  dateUpdated: null,
  dynamicLink: '',
  hasDeleted: false,
  imageUrls: [],
  landlordEmail: '',
  landlordUid: '',
  propertyFeatures: [],
  propertyFurnishing: 'furnished',
  propertyOutsideSpaces: [],
  propertyParking: 'noParking',
  propertyType: 'flat',
  propertyUid: '',
  search: {
    allowsCertificateVisibility: false,
    allowsUtilityEstimate: false,
    dateAvailable: null,
    onTheMarket: false,
    utilityCostAmount: 0,
  },
  standards: {
    eicrUrl: '',
    eicrExpiresOn: null,
    epcUrl: '',
    epcExpiresOn: null,
    gscUrl: '',
    gscExpiresOn: null,
    smokeAlarmsChecked: false,
    hasGasAppliances: false,
  },
  tenancyDepositAmount: 0,
  tenancyMonthDuration: 0,
  tenancyRentAmount: 0,
  tenancyUids: [],
  otherCharges: [],
  // Should be collected when adding details of the property
  ownership: {
    type: OwnershipTypes.soleOwned,
  },
});

export const inventoryDefault = () => ({
  dateCreated: null, // firestore.Timestamp
  dateUpdated: null, // firestore.Timestamp
  imageUrls: [],
  itemCostAmount: 0,
  itemUid: '',
  landlordUid: '',
  notes: '',
  propertyUid: '',
  title: '',
});

export const getTenancyMinDate = () => {
  const date = new Date(Date.now() + 1000 * 60 * 60 * 24 * 15);
  if (date.getDate() > 28) {
    date.setMonth(date.getMonth() + 1, 1);
  }

  return date;
};

export const tenancyDefault = () => {
  return {
    agreement: {
      dateCreated: null,
      dateUpdated: null,
      documentUid: '',
      pdfDocumentUrl: '',
      status: tenancyAgreementStatusTypes.notCreated,
      previewUrl: '',
    },
    otherCharges: [],
    dateCreated: new Date(),
    dateUpdated: new Date(),
    dates: {
      depositDue: null,
      startDate: getTenancyMinDate(),
      endDate: null, // firestore.Timestamp
      depositReturnDue: null,
      rentDueDates: [],
    },
    deposit: {
      certificateUrl: '',
      status: tenancyDepositStatusTypes.unregistered,
      tdsBatchId: '',
      tdsDepositAccountNumber: '',
    },
    durationInMonths: 12,
    dynamicLinkUrl: '',
    clauses: [],
    status: TenancyStatusTypes.future,
    landlordEmail: '',
    landlordUid: '',
    payments: {
      depositAmount: 0,
      depositAmountPaid: 0,
      depositReference: '',
      rentAmount: 0,
      rentReference: '',
      standingOrders: 0,
      standingOrderAmount: 0,
    },
    propertyUid: '',
    propertyIsOccupied: false,
    tenantCount: 0,
    tenantEmails: [],
    tenantGuarantorEmails: [],
    tenantUids: [],
    tenancyUid: null,
  };
};

export const tenantDefault = () => ({
  dateUpdated: null,
  emailAddress: '',
  firstName: '',
  fullName: '',
  guarantor: {
    emailAddress: '',
    firstName: '',
    fullName: '',
    lastName: '',
    mobileNumber: '',
    tenancyAgreementDateSigned: null,
    tenancyAgreementSigned: false,
    tenancyAgreementUrl: '',
    title: 'Mr',
  },
  hasGuarantor: false,
  lastName: '',
  linkedDate: null,
  linkedUid: '',
  linkingCode: '', // ?
  mobileNumber: '',
  payments: {
    depositAmountPaid: 0,
    depositPaidDate: null,
    depositPlaidUid: '',
    standingOrderAmountPaid: 0,
    standingOrderPlaidUid: '',
    standingOrderSetupDate: null,
  },
  tenancyUid: '',
  tenantOrder: 0,
  tenantUid: '',
  title: 'Mr',
});

export const noticeDefault = () => ({
  dateCreated: null,
  dateUpdated: null, // set on create
  hasViewed: false,
  isImage: false, // set on create
  messageFromName: 'Propa', // set on create
  messageFromUid: '0', // set on create
  messageText: '', // set on create
  noticeTitle: '', // set on create
  noticeType: 'messaging',
  noticeUid: '', // set on create
  // tenancyStatus: '', // set on create
  tenancyUid: '', // set on create
});

export const subscriptionDefault = () => ({
  amount: 0, // Decimal
  cancelledOn: null, // SwiftDate
  dateCreated: null, // SwiftDate
  dateUpdated: null, // SwiftDate
  productType: '',
  profileUid: '',
  propertyCount: 0,
  reference: '',
  startedOn: null, // SwiftDate
  status: '', // SubscriptionStatusTypes
  stripe: {
    customer: '',
    price_id: '',
    product_id: '',
    subscription_id: '',
    subscription_item_id: '',
  },
  // subscriptionUid: '',
});
export const subscriptionDefaults = () => {
  return {
    stripe: {
      subscription_item_id: '',
      customer: '',
      price_id: '',
      product_id: '',
      subscription_id: '',
    },
    status: SubscriptionStatusTypes.freeTrial,
    cancelledOn: null,
    dateCreated: null,
    productType: SubscriptionProductTypes.propertyManagement,
    startedOn: null,
    profileUid: '',
    amount: 0,
    propertyCount: 0,
    dateUpdated: null,
  };
};

export const statsDefaults = () => {
  return {
    tenants: {
      active: 0,
      total: 0,
      activeLinked: 0,
      totalLinked: 0,
    },
    subscriptionStatus: SubscriptionStatusTypes.freeTrial,
    deposits: {
      expected: 0,
      totalProtected: 0,
      expectedProtected: 0,
      total: 0,
    },
    tenancies: {
      active: 0,
      total: 0,
      terminated: 0,
      ended: 0,
      future: 0,
      depositsProtected: 0,
      agreementsSigned: 0,
    },
    // profileUid: 'fgUgOJmIoBXkzvambeNtukmVGYI2',
    subscription: {
      amount: 0,
      status: 'active',
    },
    properties: {
      onTheMarket: 0,
      total: 0,
      occupiedPercentage: 0,
      occupied: 0,
    },
    dateCreated: null,
    rent: {
      expectedActiveMonthly: 0,
      totalActiveMonthly: 0,
      total: 0,
      expected: 0,
    },
  };
};
